/**
 * Narrows an unknown value to one of an allowed set, or to undefined.
 *
 * Used on the way into a query string. Filter values reach these builders from
 * the URL, which anyone can edit, and a sort column in particular ends up in a
 * Prisma `orderBy` or — on the transactions list — concatenated into SQL. The
 * server validates too, and is the real boundary; this stops a junk value
 * making the round trip to earn a 400, and keeps an unrecognised column
 * degrading to the default order rather than an error.
 *
 * @param allowed - The values that may pass
 * @param value - The candidate, typically straight from the URL
 * @returns The value when it is allowed, otherwise undefined
 */
export function oneOf<T extends string>(allowed: readonly T[], value: unknown): T | undefined {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : undefined;
}

/**
 * Splits a multi-value search parameter into its candidate values.
 *
 * Accepts both encodings that can reach here: `state=active,disabled`, which is
 * what the admin lists write because it survives a copy-paste out of the
 * address bar legibly, and an array, which is what the router's default JSON
 * encoding parses to and what a hand-written `?state=a&state=b` produces.
 */
function splitList(value: unknown): string[] {
  const parts = Array.isArray(value) ? value : [value];
  const values: string[] = [];

  for (const part of parts) {
    if (typeof part !== 'string') continue;
    for (const piece of part.split(',')) {
      const trimmed = piece.trim();
      if (trimmed && !values.includes(trimmed)) values.push(trimmed);
    }
  }

  return values;
}

/**
 * Narrows an unknown value to a list drawn from an allowed set, or to undefined.
 *
 * The multi-value counterpart to {@link oneOf}, and lenient for the same
 * reason: this runs on the way out of a hand-editable URL, where there is no
 * way to report an error to the reader. An unrecognised value is dropped and
 * the rest of the filter still applies, so a mistyped link degrades to a wider
 * list rather than an error card. The API rejects what it does not recognise
 * and remains the real boundary.
 *
 * @param allowed - The values that may pass
 * @param value - The candidate, typically straight from the URL
 * @returns The allowed values that were named, or undefined if none were
 */
export function manyOf<T extends string>(allowed: readonly T[], value: unknown): T[] | undefined {
  const values = splitList(value).filter((entry): entry is T =>
    (allowed as readonly string[]).includes(entry),
  );
  return values.length > 0 ? values : undefined;
}

/**
 * Narrows an unknown value to a list of non-empty strings, or to undefined.
 *
 * For a filter with no fixed set to check against — `role` is a row in the
 * database rather than an enum, so the list of valid values is not known to the
 * client.
 */
export function manyStrings(value: unknown): string[] | undefined {
  const values = splitList(value);
  return values.length > 0 ? values : undefined;
}

/**
 * Writes a multi-value filter back into the URL.
 *
 * An empty selection returns undefined rather than an empty string: "no filter"
 * must leave the parameter out entirely, since `?state=` in the URL would claim
 * a filter the reader never applied.
 */
export function toCsv(values: readonly string[] | undefined): string | undefined {
  return values && values.length > 0 ? values.join(',') : undefined;
}
